export type DataTypeKind =
    | 'string'
    | 'number'
    | 'boolean'
    | 'stringLiteral'
    | 'numberLiteral'
    | 'object'
    | 'array'
    | 'union'
    | 'generic'
    | 'shape'
    | 'null'
    | 'function'
    | 'secret'
    | 'optional';

export interface DataTypeBase {
    kind: DataTypeKind;
    name?: string;
}

export interface StringDataType extends DataTypeBase {
    kind: 'string';
}

export interface NumberDataType extends DataTypeBase {
    kind: 'number';
}

export interface BooleanDataType extends DataTypeBase {
    kind: 'boolean';
}

export interface StringLiteralDataType extends DataTypeBase {
    kind: 'stringLiteral';
    stringLiteral: string;
}

export interface NumberLiteralDataType extends DataTypeBase {
    kind: 'numberLiteral';
    numberLiteral: number;
}

export interface ObjectDataType extends DataTypeBase {
    kind: 'object';
    restriction: 'exact' | 'open';
    properties: { [key: string]: DataType };
    optionalProperties?: string[];
}

export interface ArrayDataType extends DataTypeBase {
    kind: 'array';
    subType: DataType;
}

export interface UnionDataType extends DataTypeBase {
    kind: 'union';
    subTypes: [DataType, ...DataType[]];
}

export interface GenericDataType extends DataTypeBase {
    kind: 'generic';
    identifier: string;
    extends?: DataType;
    resolvedType?: DataType;
}

export interface ShapeDataType extends DataTypeBase {
    kind: 'shape';
    restriction?: DataType;
}

export interface NullDataType extends DataTypeBase {
    kind: 'null';
}

export interface FunctionDataType extends DataTypeBase {
    kind: 'function';
    inputTypes: { [portName: string]: DataType };
    outputTypes: { [portName: string]: DataType };
}

export interface SecretDataType extends DataTypeBase {
    kind: 'secret';
}

export interface OptionalDataType extends DataTypeBase {
    kind: 'optional';
    innerType: DataType;
}

export type DataType =
    | StringDataType
    | NumberDataType
    | BooleanDataType
    | StringLiteralDataType
    | NumberLiteralDataType
    | ObjectDataType
    | ArrayDataType
    | UnionDataType
    | GenericDataType
    | ShapeDataType
    | NullDataType
    | FunctionDataType
    | SecretDataType
    | OptionalDataType;

export function typeAMatchesTypeB(aParamType: DataType, b: DataType): boolean {
    const a: DataType = unwrapAllResolvedGenerics(aParamType);

    // If a is a union and b is not, check that every subtype of a satisfies b
    if (a.kind === 'union' && b.kind !== 'union') {
        return a.subTypes.every(subType => typeAMatchesTypeB(subType, b));
    }

    // Optional target: T matches Optional<T>, null matches Optional<T>, Optional<T> matches Optional<T>
    if (b.kind === 'optional') {
        if (a.kind === 'optional') {
            return typeAMatchesTypeB(a.innerType, b.innerType);
        }
        if (a.kind === 'null') {
            return true;
        }
        return typeAMatchesTypeB(a, b.innerType);
    }

    switch (b.kind) {
        case 'string':
            return a.kind === 'string' || a.kind === 'stringLiteral';
        case 'number':
            return a.kind === 'number' || a.kind === 'numberLiteral';
        case 'boolean':
            return a.kind === 'boolean';
        case 'stringLiteral':
            return a.kind === 'stringLiteral' && a.stringLiteral === b.stringLiteral;
        case 'numberLiteral':
            return a.kind === 'numberLiteral' && a.numberLiteral === b.numberLiteral;
        case 'object': {
            if (a.kind !== 'object') {
                return false;
            } else {
                const bPropertiesList = Object.entries(b.properties);
                const doAllPropertiesFromBMatch = bPropertiesList.every(([key, value]) => {
                    const aProperty = a.properties[key];
                    if (aProperty === undefined) {
                        return false;
                    }
                    return typeAMatchesTypeB(value, aProperty);
                });
                switch (b.restriction) {
                    case 'exact':
                        return doAllPropertiesFromBMatch && bPropertiesList.length === Object.keys(a.properties).length;
                    case 'open':
                        return doAllPropertiesFromBMatch;
                }
                return false;
            }
        }
        case 'array':
            if (a.kind !== 'array') {
                return false;
            }
            return typeAMatchesTypeB(a.subType, b.subType);
        case 'union':
            if (a.kind === 'union') {
                return a.subTypes.every(subType => typeAMatchesTypeB(subType, b));
            }
            return b.subTypes.some(subType => typeAMatchesTypeB(a, subType));
        case 'generic':
            if (b.resolvedType) {
                return typeAMatchesTypeB(b.resolvedType, a);
            } else if (b.extends) {
                return typeAMatchesTypeB(a, b.extends);
            } else {
                return true;
            }
        case 'shape': {
            if (a.kind !== 'shape') {
                return false;
            }

            // If there is no restriction from the receiving type it is automatically valid
            if (b.restriction === undefined) {
                return true;
            }

            // If b is restricting and a has no restriction it can't be valid
            if (a.restriction === undefined) {
                return false;
            }

            // Do actual comparison
            return typeAMatchesTypeB(a.restriction, b.restriction);
        }
        case 'null':
            return a.kind === 'null';
        case 'secret':
            return a.kind === 'secret';
        case 'function':
            if (a.kind !== 'function') {
                return false;
            }
            // Functions match if their signatures are compatible
            // A function A matches expected type B if:
            // - A has at least all the input ports that B requires (extra inputs are allowed as pass-through)
            // - A has at least all the output ports that B requires (extra outputs are allowed)
            // - Each matching port has compatible types

            // Check input types - A must have all inputs from B, but can have more
            const bInputKeys = Object.keys(b.inputTypes);
            const inputsMatch = bInputKeys.every(key => {
                const aInputType = a.inputTypes[key];
                if (aInputType === undefined) {
                    return false; // Required input port is missing
                }
                return typeAMatchesTypeB(aInputType, b.inputTypes[key]);
            });
            if (!inputsMatch) {
                return false;
            }

            // Check output types - A must have all outputs from B, but can have more
            const bOutputKeys = Object.keys(b.outputTypes);
            return bOutputKeys.every(key => {
                const aOutputType = a.outputTypes[key];
                if (aOutputType === undefined) {
                    return false; // Required output port is missing
                }
                return typeAMatchesTypeB(aOutputType, b.outputTypes[key]);
            });
    }
}

export function getStringRepresentationFromType(type: DataType): string {
    if (type.name) {
        return type.name;
    }
    switch (type.kind) {
        case 'string':
            return 'Text';
        case 'number':
            return 'Number';
        case 'boolean':
            return 'Boolean';
        case 'stringLiteral':
            return type.stringLiteral;
        case 'numberLiteral':
            return type.numberLiteral.toString();
        case 'object':
            return `Object:\n    ${Object.values(type.properties).map(getStringRepresentationFromType).join('\n    ')}`;
        case 'array':
            return `List of:\n    ${getStringRepresentationFromType(type.subType)}`;
        case 'union':
            const typeStrings = type.subTypes.map(getStringRepresentationFromType);
            switch (typeStrings.length) {
                case 0: {
                    return 'No options for this type';
                }
                case 1: {
                    return typeStrings[0];
                }
                default: {
                    return typeStrings.slice(0, -1).join(', ') + ' or ' + typeStrings.slice(-1);
                }
            }
        case 'generic':
            if (type.resolvedType) {
                return getStringRepresentationFromType(type.resolvedType);
            }
            return `Generic Type: ${type.identifier}`;
        case 'shape':
            return `Shape:\n ${type.restriction ? getStringRepresentationFromType(type.restriction) : ''}`;
        case 'null':
            return 'Null';
        case 'secret':
            return 'Secret';
        case 'optional':
            return `Optional ${getStringRepresentationFromType(type.innerType)}`;
        case 'function':
            const inputTypes = Object.entries(type.inputTypes)
                .map(([name, dataType]) => `${name}: ${getStringRepresentationFromType(dataType)}`)
                .join(', ');
            const outputTypes = Object.entries(type.outputTypes)
                .map(([name, dataType]) => `${name}: ${getStringRepresentationFromType(dataType)}`)
                .join(', ');
            return `Function (${inputTypes}) → (${outputTypes})`;
    }
}

export function unwrapAllResolvedGenerics(dataType: DataType): DataType {
    switch (dataType.kind) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'stringLiteral':
        case 'numberLiteral':
        case 'shape':
        case 'null':
        case 'secret':
            return dataType;
        case 'function':
            return {
                ...dataType,
                inputTypes: Object.fromEntries(
                    Object.entries(dataType.inputTypes).map(([key, valueType]) => [key, unwrapAllResolvedGenerics(valueType)]),
                ),
                outputTypes: Object.fromEntries(
                    Object.entries(dataType.outputTypes).map(([key, valueType]) => [key, unwrapAllResolvedGenerics(valueType)]),
                ),
            };
        case 'object':
            return {
                ...dataType,
                properties: Object.fromEntries(
                    Object.entries(dataType.properties).map(([key, valueType]) => [key, unwrapAllResolvedGenerics(valueType)]),
                ),
            };
        case 'array':
            return {
                ...dataType,
                subType: unwrapAllResolvedGenerics(dataType.subType),
            };
        case 'union':
            return {
                ...dataType,
                subTypes: dataType.subTypes.map(unwrapAllResolvedGenerics) as [DataType, ...DataType[]],
            };
        case 'generic':
            if (dataType.resolvedType) {
                return unwrapAllResolvedGenerics(dataType.resolvedType);
            }
            return dataType;
        case 'optional':
            return {
                ...dataType,
                innerType: unwrapAllResolvedGenerics(dataType.innerType),
            };
    }
}
